import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import FormGroup from '../../../FormGroup';
import SelectGoalTree from '../../../SelectGoalTree/SelectGoalTree.container';
import TreeGoalActions from '../../../TreeGoalActions/TreeGoalActions';

const toStringId = (value) => value === undefined || value === null ? '' : String(value);

const PostbackGoalActions = ({ 
    goalActionIds, 
    goalActions, 
    modalIsVisible, 
    getGoalActions, 
    getListCurGoals, 
    onAction, 
    t 
}) => {
    const [state, setState] = useState({
        curIds: (goalActionIds || []).map(toStringId),
        list: [],
        isGlobal: !(goalActionIds && goalActionIds.length),
        modalIsVisible: modalIsVisible || false
    });

    const prevGoalActionIdsRef = useRef(goalActionIds);
    const prevGoalActionsRef = useRef(goalActions);

    const getList = useCallback((goalActionsData, currentList, currentCurIds) => {
        let list = [...(currentList || [])];

        (currentCurIds || []).map((id) => {
            let exist = !!list.find((item) => toStringId(item.id) === toStringId(id));

            if (!exist) {
                let goalAction = (goalActionsData || []).filter((item) => toStringId(item.id) === toStringId(id)) || [];
                list = list.concat(goalAction);
            }
        });

        return list;
    }, []);

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            list: getList(goalActions, prevState.list, prevState.curIds)
        }));
    }, [goalActions, getList]);

    useEffect(() => {
        if (state.curIds.length && getGoalActions) {
            const hasAllGoalActions = state.curIds.every((id) =>
                (goalActions || []).some(goalAction => toStringId(goalAction.id) === toStringId(id))
            );

            if (!hasAllGoalActions) {
                getGoalActions(state.curIds);
            }
        }
    }, [state.curIds, goalActions, getGoalActions]);

    useEffect(() => {
        const isChangeGoalActionIds = JSON.stringify(prevGoalActionIdsRef.current) !== JSON.stringify(goalActionIds);
        
        if (isChangeGoalActionIds) {
            setState(prevState => {
                const newCurIds = [...(goalActionIds || [])].map(toStringId);
                const newList = getList(goalActions, prevState.list, newCurIds);
                
                if (!newList.length && getGoalActions && newCurIds.length) {
                    getGoalActions(newCurIds);
                }

                return {
                    ...prevState,
                    curIds: newCurIds,
                    list: newList,
                    isGlobal: !newCurIds.length
                };
            });
        }

        prevGoalActionIdsRef.current = goalActionIds;
    }, [goalActionIds, goalActions, getGoalActions, getList]);

    const toggleModal = useCallback((modifyGlobal) => {
        setState(prevState => ({
            ...prevState,
            modalIsVisible: !prevState.modalIsVisible,
            isGlobal: modifyGlobal ? false : prevState.isGlobal
        }));
    }, []);

    const onDeleteGoal = useCallback((id) => {
        setState(prevState => {
            let list = JSON.parse(JSON.stringify(prevState.list));
            let curIds = JSON.parse(JSON.stringify(prevState.curIds));
            const stringId = toStringId(id);
            const indexList = list.findIndex((item) => toStringId(item.id) === stringId);
            const indexIds = curIds.indexOf(stringId);

            if (indexList !== -1) list.splice(indexList, 1);
            if (indexIds !== -1) curIds.splice(indexIds, 1);

            if (onAction) {
                onAction(curIds);
            }

            return {
                ...prevState,
                curIds,
                list,
                isGlobal: !curIds.length
            };
        });
    }, [onAction]);

    const onChangeGlobal = useCallback((value) => {
        const curIds = !!value ? [] : state.curIds;

        if (onAction) {
            onAction(curIds);
        }

        setState(prevState => ({
            ...prevState,
            isGlobal: !!value
        }));
    }, [state.curIds, onAction]);

    const addGoalActions = useCallback((selectedGoalActions) => {
        setState(prevState => {
            let curIds = [...prevState.curIds];
            let list = [...prevState.list];

            curIds = curIds.concat(selectedGoalActions.map(item => toStringId(item.id)));
            list = list.concat(selectedGoalActions);

            if (onAction) {
                onAction(curIds);
            }

            return {
                ...prevState,
                curIds,
                list,
                isGlobal: false
            };
        });
    }, [onAction]);

    const staticNames = t?.postbackPage?.creatingEditing || {};
    const staticActions = t?.actions || {};

    return (
        <div className="form-horizontal">
            <h3 className="bold subtitle">{staticNames.goalTitle}</h3>
            <div>
                <button
                    className={`roundButton bold ${state.isGlobal ? 'roundButton--active' : ''}`}
                    type="button"
                    onClick={() => onChangeGlobal(1)}
                >
                    {staticNames.all}
                </button>
                <button
                    className={`roundButton bold ${state.isGlobal ? '' : 'roundButton--active'}`}
                    type="button"
                    onClick={() => toggleModal(true)}
                >
                    {staticActions.add}
                </button>
            </div>
           
            {state.curIds.length && !state.isGlobal
                ? (
                    <FormGroup>
                        <pre className="m-t-sm postback__active-goal">
                            <TreeGoalActions
                                edit={true}
                                onAction={onDeleteGoal}
                                data={getListCurGoals ? getListCurGoals(state.list, state.curIds) : []}
                            />
                        </pre>
                    </FormGroup>
                )
                : null
            }
            <SelectGoalTree
                modalIsVisible={state.modalIsVisible}
                blockedIdGoalActions={state.curIds}
                onCloseModal={toggleModal}
                onConfirm={addGoalActions}
            />
        </div>
    );
};

PostbackGoalActions.propTypes = {
    goalActionIds: PropTypes.array,
    goalActions: PropTypes.array,
    modalIsVisible: PropTypes.bool,
    getGoalActions: PropTypes.func,
    getListCurGoals: PropTypes.func,
    onAction: PropTypes.func,
    t: PropTypes.object
};

PostbackGoalActions.defaultProps = {
    goalActionIds: [],
    goalActions: [],
    modalIsVisible: false,
    getGoalActions: () => {},
    getListCurGoals: () => [],
    onAction: () => {},
    t: {}
};

export default withTranslation(PostbackGoalActions);
